/**
 * Paged-list contract (PagedQueryDto / PagedResponseDto). Traps it encodes:
 * only the first sort descriptor is used; `logic` is ignored (always AND);
 * fields outside the module allow-list are silently ignored by the server.
 */
import { z, type ZodType } from 'zod';

export type FilterOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith';

export interface FilterDescriptor {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean;
}

export interface SortDescriptor {
  field: string;
  dir: 'asc' | 'desc';
}

export interface PagedQuery {
  skip: number;
  take: number;
  sort?: SortDescriptor | null;
  filters?: FilterDescriptor[];
}

export function encodePagedQuery(q: PagedQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {
    skip: Math.max(0, Math.floor(q.skip)),
    take: Math.max(1, Math.floor(q.take)),
  };
  if (q.sort) out.sort = JSON.stringify({ field: q.sort.field, dir: q.sort.dir });
  const filters = (q.filters ?? []).filter((f) => f.value !== '' && f.value !== null && f.value !== undefined);
  if (filters.length) out.filter = JSON.stringify(filters);
  return out;
}

export interface Paged<T> {
  data: T[];
  total: number;
  skip: number;
  take: number;
}

export const pagedSchema = <T>(item: ZodType<T>) =>
  z.object({
    data: z.array(item),
    total: z.coerce.number(),
    skip: z.coerce.number().default(0),
    take: z.coerce.number().default(0),
  });
